import { useTranslation } from "react-i18next";
import type { Currency } from "@nh/shared";
import { PricingCard } from "@/shared/ui/ui";
import { FRANCHISE_CONTENT } from "../franchise.content";
import { franchisePrice } from "../franchise.pricing";
import { useFranchiseSettings } from "../useFranchiseSettings";

export interface FranchisePricingCardProps {
  /** Moneda activa del sitio: la del selector de la barra superior. */
  currency: Currency;
}

/**
 * La tarjeta de compra que cierra la landing.
 *
 * ── El botón NAVEGA, ya no cobra (Anahí, 26-ago) ─────────────────────
 * Antes pedía el link de Stripe aquí mismo y mandaba el navegador a
 * pagar. El flujo nuevo tiene un paso en medio: "con el botón de
 * adquirir tu franquicia, te debe redirigir a ADQUIRIR tu franquicia" —
 * la pantalla con la ficha técnica y el primer stock (el paquete
 * mayorista del modal). El cobro se genera ALLÁ, con la franquicia y el
 * paquete sumados en un solo pedido.
 *
 * De paso se cierra el hueco que esta tarjeta documentaba: la landing
 * es pública, pero la pantalla de adquisición exige sesión — el pago ya
 * no puede llegar a Stripe sin dueño.
 *
 * ── La forma ya estaba migrada ───────────────────────────────────────
 * `PricingCard` de `@/shared/ui/ui` es la misma pieza del sistema
 * actual. Este archivo no la reescribe: le da el contenido de la
 * franquicia y le cuelga la dirección.
 */
export function FranchisePricingCard({ currency }: FranchisePricingCardProps) {
  const { t } = useTranslation();
  const { prices } = useFranchiseSettings();
  const { text: price } = franchisePrice(currency, prices);
  const { beneficiosTitle, beneficios, ventajasTitle, ventajas, costTagline, costUnit, ctaLabel } =
    FRANCHISE_CONTENT.content;

  return (
    <div className="w-full">
      <PricingCard
        title={t("Franquicias Digitales")}
        description={t(costTagline)}
        price={price}
        currency={currency}
        priceSuffix={t(costUnit)}
        features={[
          {
            title: t(beneficiosTitle),
            items: beneficios.map((benefit) => t(benefit.title)),
          },
          {
            title: t(ventajasTitle),
            items: ventajas.map((row) => `${row.pct} · ${t(row.label)}`),
          },
        ]}
        buttonText={t(ctaLabel)}
        /*
         * Un enlace, no un botón: la acción volvió a ser NAVEGAR.
         * `PricingCard` pinta uno u otro según qué reciba.
         */
        buttonTo="/franquicias/adquirir"
      />
    </div>
  );
}
